import { Theme, themes } from '@teetsh/app/src/containers/Sidebar/themes';
import React, { useEffect, useState } from 'react';
import { NavLink } from '@teetsh/app/src/components/NavLink';
import tw, { css } from 'twin.macro';
import { ChevronDownIcon as ChevronDown } from '@heroicons/react/24/outline';
import { LockClosedIcon as LockClosed } from '@heroicons/react/24/outline';
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';

export interface MenuItemProps {
  title: string;
  route: string;
  id?: string;
  icon?: React.ComponentType<any>;
  disabled?: boolean;
  hidden?: boolean;
  subItems?: MenuItemProps[];
  active?: boolean;
}

interface Props {
  item: MenuItemProps;
  theme?: Theme;
  minimal?: boolean;
  onClose?: () => void;
}

const getItemStyle = (item: MenuItemProps) => [item.hidden && tw`hidden`];

const MenuIconStyled = styled('div', {
  shouldForwardProp: prop => isPropValid(prop),
})<{ minimal?: boolean }>`
  ${tw`h-6 w-6 shrink-0 -my-px`}
  ${p => !p.minimal && tw`mr-2 -ml-1`}
`;

const baseLinkStyle = css`
  ${tw`flex items-center transition duration-150 no-underline whitespace-nowrap border border-transparent rounded-lg`}
  ${tw`py-2`}
`;

const minimalStyle = css`
  ${tw`pl-2 pr-1 mx-1`}
`;

const fullStyle = css`
  ${tw`px-3`}
`;

export function MenuItem(props: Props) {
  const { item, theme = 'light', minimal, onClose } = props;

  // Initialize open state based on active status
  const [isSubItemOpen, setIsSubItemOpen] = useState(item.active);

  // Effect to synchronize open state with active status when path changes
  useEffect(() => {
    setIsSubItemOpen(item.active);
    // Only react to active status change derived from pathname/item
  }, [item.active]);

  const icon = item.disabled ? LockClosed : item.icon;
  const hasSubItems = (item?.subItems || []).length > 0;

  const commonContent = (
    <>
      <MenuIconStyled as={icon as any} minimal={minimal} />
      <span
        css={[
          tw`flex-grow whitespace-normal`,
          minimal && tw`invisible whitespace-nowrap`,
        ]}
      >
        {item.title}
      </span>
    </>
  );

  const commonStyles = [
    baseLinkStyle,
    themes[theme].navLink,
    minimal ? minimalStyle : fullStyle,
  ];

  // Specific active styles for NavLink (when it's the final destination)
  const activeNavLinkStyle =
    item.active && !hasSubItems && themes[theme].activeNavLink;
  // Different active style for the button (parent of active subitem or active itself)
  const activeButtonOrParentStyle =
    item.active && hasSubItems && tw`text-white`; // Just brighten text

  return (
    <div css={[tw`mb-2 last:mb-0`, getItemStyle(item)]}>
      {hasSubItems ? (
        <button
          type="button"
          aria-expanded={isSubItemOpen}
          aria-label={`Ouvrir/Fermer le sous-menu ${item.title}`}
          css={[
            ...commonStyles,
            activeButtonOrParentStyle,
            tw`w-full text-left`,
          ]}
          onClick={() => setIsSubItemOpen(!isSubItemOpen)}
        >
          {commonContent}
          <span css={[minimal && tw`invisible`]}>
            <ChevronDown
              css={[
                tw`h-5 w-5 transition duration-200`,
                themes[theme].navLinkTreeChevron,
                isSubItemOpen && tw`rotate-180`,
              ]}
            />
          </span>
        </button>
      ) : (
        <NavLink
          aria-label={`Naviguer vers ${item.title}`}
          css={[...commonStyles, activeNavLinkStyle]}
          onClick={onClose}
          to={item.route}
        >
          {commonContent}
        </NavLink>
      )}
      {/* Sub-items rendering logic remains largely the same */}
      {item.subItems && isSubItemOpen && !minimal && (
        <div tw="text-white pl-7 pb-2 space-y-1 mt-1">
          {(item.subItems || []).map(subItem => (
            <div
              key={subItem.route || subItem.title}
              css={[getItemStyle(subItem)]}
            >
              <NavLink
                aria-label={`Naviguer sur ${subItem.title}`}
                css={[
                  tw`py-1 px-3 block text-reverse-moderate transition duration-150 no-underline rounded-lg active:border-reverse-moderate`,
                  !subItem.active && tw`hover:bg-reverse-quiet`,
                  subItem.active &&
                    tw`bg-reverse-moderate text-white font-bold`,
                ]}
                onClick={onClose}
                to={subItem.route}
              >
                <span css={[tw`flex items-center`]}>
                  <span tw="text-sm font-medium">{subItem.title}</span>
                  {subItem.disabled && <LockClosed tw="size-5 ml-auto" />}
                </span>
              </NavLink>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
